import PropTypes from 'prop-types';
import styled from 'helpers/styles';
import ComponentStyles from 'styles/mixins/Component.styles';
import Chip from '@mui/material/Chip';
import constants from 'helpers/constants';
import utils from 'helpers/utils';

const StyledChip = styled(Chip)
  .attrs((props) => {
    let attr = props;

    const outlinedColorVars = props.theme.color2Css('Chip', null, props.color, {
      varPrefix: 'chip',
      variables: true,
      interactive: props.clickable,
      focusable: Boolean(props.onDelete),
      type: constants.colorTypes.outlined
    });

    attr = utils.styleProps(attr, outlinedColorVars.vars, {outlinedColor: outlinedColorVars.css});

    const transparentColorVars = props.theme.color2Css('Chip', null, props.color, {
      varPrefix: 'chip',
      variables: true,
      interactive: props.clickable,
      focusable: Boolean(props.onDelete),
      type: constants.colorTypes.transparent
    });

    attr = utils.styleProps(attr, transparentColorVars.vars, {transparentColor: transparentColorVars.css});

    const filledColorVars = props.theme.color2Css('Chip', null, props.color, {
      varPrefix: 'chip',
      variables: true,
      interactive: props.clickable,
      focusable: Boolean(props.onDelete),
      type: constants.colorTypes.button
    });

    attr = utils.styleProps(attr, filledColorVars.vars, {filledColor: filledColorVars.css});

    const vars = {
      '--chip-outlined-disabled-color': props.theme.state2Color(props.color, 'default', constants.colorTypes.outlined, constants.colorTargets.foreground)
    };

    return utils.styleProps(attr, vars);
  })`
  overflow: hidden;
  border-radius: ${props => props.theme.radius(1)};
  font-size: ${props => props.theme.fontSize(16)};
  
  &.Chip {
    .MuiChip-label {
      ${props => props.theme.font2Css('chipLabel')};
    }
    
    .Chip-badge {
      .MuiBadge-badge {
        height: ${props => props.theme.layout(6)};
        width: ${props => props.theme.layout(6)};
        min-width: ${props => props.theme.layout(6)};
        right: ${props => props.theme.layout(1)};
        top: ${props => props.theme.layout(1)};
      }
      
      .Icon {
        font-size: inherit;
      }
    }
  }

  &.Chip-fullWidth {
    width: 100%;  
  }
  
  &.Chip-size-large {
    font-size: ${props => props.theme.fontSize(13)};
    border-radius: ${props => props.theme.radius(2)};
    height: ${props => props.theme.layout(36)};

    .MuiChip-label {
      font-size: inherit;
      padding: 0 ${props => props.theme.layout('1.25sp')};
    }
    .MuiChip-icon, .MuiChip-avatar {
      font-size: ${props => props.theme.fontSize(16)};
      margin-left: ${props => props.theme.layout(8)};
      margin-right: ${props => props.theme.layout(-2)};
    }
    .MuiChip-deleteIcon {
      font-size: ${props => props.theme.fontSize(16)};
      margin-left: ${props => props.theme.layout(-2)};
      margin-right: ${props => props.theme.layout(8)};
    }
  }

  &.Chip-size-medium {
    font-size: ${props => props.theme.fontSize(13)};
    height: ${props => props.theme.layout(24)};
    .MuiChip-label {
      font-size: inherit;
      padding: 0 ${props => props.theme.layout(6)};
    }
    .MuiChip-icon, .MuiChip-avatar {
      font-size: ${props => props.theme.fontSize(16)};
      margin-right: ${props => props.theme.layout(-2)};
    }
    .MuiChip-deleteIcon {
      font-size: ${props => props.theme.fontSize(16)};
      margin-left: ${props => props.theme.layout(-2)};
    }
  }
  
  &.Chip-size-small {
    border-radius: ${props => props.theme.radius(0.5)};
    font-size: ${props => props.theme.fontSize(12)};
    height: ${props => props.theme.layout(18)};
    .MuiChip-label {
      font-size: inherit;
      line-height: ${props => props.theme.lineHeight('1f')};
      padding: 0 ${props => props.theme.layout(4)};
    }
    .MuiChip-icon, .MuiChip-avatar {
      font-size: ${props => props.theme.fontSize(12)};
      margin-right: ${props => props.theme.layout(-2)};
    } 
    .MuiChip-deleteIcon {
      font-size: ${props => props.theme.fontSize(12)};
      margin-left: ${props => props.theme.layout(-2)};
    }
  }

  &.Chip-outlined {
    ${props => props.$css.outlinedColor}
    
    &.Chip-color-secondary {
      color: ${props => props.theme.property('palette.action.active')};

      .MuiChip-deleteIcon {
        &:not(${props => props.theme.state2Selector('MuiChip-deleteIcon', null, ['hover', 'focus', 'focusVisible', 'active', 'selected'], '')}) {
          color: ${props => props.theme.property('palette.action.active')};
        }
      }
    }
    
    ${props => props.theme.state2Selector('Chip', null, 'disabled')} {
      border-color: ${props => props.theme.property('palette.chip.defaultEnabledBorder')} !important;
      color: ${props => props.$css.$var('--outlined-disabled-color', false)} !important;
    }

    &.Chip {
      border-color: ${props => props.theme.property('palette.chip.defaultEnabledBorder')};
    }
  }
  
  &.Chip-transparent {
    ${props => props.$css.transparentColor}

    .MuiChip-deleteIcon {
      color: inherit;
    }
  }

  &.Chip-filled {
    ${props => props.$css.filledColor}
  }

  &.Chip-radius-round {
    border-radius: ${props => props.theme.radius(2)};

    &.Chip-size-small {
      border-radius: ${props => props.theme.radius(1)};
    }
  }
  
  ${ComponentStyles}
`;

StyledChip.propTypes = {
  theme: PropTypes.object
};

export default StyledChip;

